import {Reducer, updateFactory} from '@modifi/redux'
import {
  aUserFeedBackSubmitFailed,
  aUserFeedBackSubmitSuccessful,
  COMPANY_CIN_CODE,
  COMPANY_INDUSTRY,
  COMPANY_LEGAL_FORM,
} from '@modifi/plugin-onboarding'
import {ProviderIdType, PublicRegisteredCompaniesRetrievalStatus} from '@modifi/enums'
import {getAuthenticatedThemisClient} from '@modifi/onboarding-api-clients'
import {aPush} from '@modifi/router'

import {
  COMPANY_NAME_SELECT,
  SECTION_PRE_UNDERWRITING_COMPANY_INTRODUCTION,
  COMPANY_FOUNDATION_MONTH,
  COMPANY_FOUNDATION_YEAR,
  COMPANY_SELECT_NONE_TEXT,
  PRE_UNDERWRITING_COMPANY_FINANCIALS,
} from '../constants'
import {
  selectCompanyCinCode,
  aUpdateRegistrationDetails,
} from '../../../../data/company-registration'
import {
  aFetchPublicRegisteredCompanies,
  aFetchPublicRegisteredCompany,
  selectPublicRegisteredCompanies,
  selectPublicRegisteredCompaniesRetrievalStatus,
} from '../../../../data/public-registered-companies'
import {
  aFetchCompany,
  aUpdateCompany,
  mapCompanyToRequest,
  selectCompanyExternalId,
  selectCompanyFoundationDate,
  selectCompanyIndustry,
  selectCompanyLegalForm,
  selectCompanyName,
  selectCurrentCompaniesCountryCode,
} from '../../../../data/company'
import {getLogger} from '../../../../utils'
import {ROUTE_INTRO} from '../../../../routes/constants'

const apiClient = getAuthenticatedThemisClient()

const logger = getLogger('pre-underwriting-company-introduction-page')

export const selectInitialFormData = (
  state: Record<string, any>
): {initialValues: Record<string, any>; defaultValues: Record<string, any>} => {
  const defaultValues = {}
  const initialValues = {
    [COMPANY_CIN_CODE]: '',
    [COMPANY_LEGAL_FORM]: '',
    [COMPANY_FOUNDATION_MONTH]: '',
    [COMPANY_FOUNDATION_YEAR]: '',
    [COMPANY_INDUSTRY]: null,
  }

  const companyCinCode = selectCompanyCinCode(state)
  if (companyCinCode) {
    Object.assign(initialValues, {
      [COMPANY_CIN_CODE]: companyCinCode,
    })
  }

  const legalForm = selectCompanyLegalForm(state)
  if (legalForm) {
    Object.assign(initialValues, {
      [COMPANY_LEGAL_FORM]: legalForm,
    })
  }

  const countryCode = selectCurrentCompaniesCountryCode(state)
  const companyName = selectCompanyName(state)
  const retrievalStatus = selectPublicRegisteredCompaniesRetrievalStatus(state)
  if (countryCode && companyName) {
    const publicRegisteredCompanies = selectPublicRegisteredCompanies(state) || []
    if (retrievalStatus === PublicRegisteredCompaniesRetrievalStatus.PENDING_RESOLUTION) {
      if (companyCinCode || legalForm) {
        // if company was not resolved last time but other detils were filled then that
        // means user would have chosen none in company name select
        Object.assign(defaultValues, {
          [COMPANY_NAME_SELECT]: COMPANY_SELECT_NONE_TEXT,
        })
      } else {
        Object.assign(defaultValues, {
          [COMPANY_NAME_SELECT]: publicRegisteredCompanies[0]?.providerCompanyId,
        })
      }
    } else {
      Object.assign(initialValues, {
        [COMPANY_NAME_SELECT]: publicRegisteredCompanies[0]?.providerCompanyId,
      })
    }
  }

  const foundationDate = selectCompanyFoundationDate(state)
  if (foundationDate) {
    const month = new Date(foundationDate).getMonth()
    const year = new Date(foundationDate).getFullYear()
    Object.assign(initialValues, {
      [COMPANY_FOUNDATION_MONTH]: month,
      [COMPANY_FOUNDATION_YEAR]: year,
    })
  }

  const industry = selectCompanyIndustry(state)
  if (industry?.code) {
    Object.assign(initialValues, {
      [COMPANY_INDUSTRY]: industry.code,
    })
  }

  return {
    initialValues,
    defaultValues,
  }
}

const {actions, selectors, reducer} = updateFactory({
  actionNamePrefix: 'pages/company-introduction/submit',
  rootSelector: state => state?.pages?.preUnderwriting?.companyIntroduction,
  submitRequest:
    (a, changes: Record<string, any>) =>
    async (dispatch, getState): Promise<void> => {
      const currentCompanyId = selectCompanyExternalId(getState())
      const prevCin = selectCompanyCinCode(getState())
      logger.info(
        'Submitting pre-underwriting company introduction details for company=%s',
        currentCompanyId
      )
      logger.debug('changes: %o', changes)

      if (Object.keys(changes).length > 0) {
        dispatch(a.pendingAction())

        const {companyNameSelect, cin, ...companyChanges} = mapCompanyToRequest(changes)
        const retrievalStatus = selectPublicRegisteredCompaniesRetrievalStatus(getState())

        try {
          if (retrievalStatus === PublicRegisteredCompaniesRetrievalStatus.PENDING_RESOLUTION) {
            if (companyNameSelect && companyNameSelect !== COMPANY_SELECT_NONE_TEXT) {
              await dispatch(aFetchPublicRegisteredCompany(companyNameSelect))
            } else if (cin) {
              if (prevCin && currentCompanyId) {
                const registrationDetailsResponse =
                  await apiClient.CompanyRegistrationDetails.update({
                    body: {cin},
                  })?.then(res => res.data())
                dispatch(aUpdateRegistrationDetails(registrationDetailsResponse))
              } else {
                await dispatch(aFetchPublicRegisteredCompany(cin, ProviderIdType.CIN))
              }
            }
          }
          await dispatch(aUpdateCompany(companyChanges))
        } catch (e: any) {
          dispatch(a.errorAction(e))
          dispatch(aUserFeedBackSubmitFailed(SECTION_PRE_UNDERWRITING_COMPANY_INTRODUCTION))
          return
        }
        dispatch(a.successAction())
        dispatch(aUserFeedBackSubmitSuccessful(SECTION_PRE_UNDERWRITING_COMPANY_INTRODUCTION))
        if (retrievalStatus === PublicRegisteredCompaniesRetrievalStatus.PENDING_RESOLUTION) {
          dispatch(aFetchPublicRegisteredCompanies())
        }
        await aFetchCompany()

        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'preonboarding_company_introduction_submit',
        })
        dispatch(aPush(ROUTE_INTRO, {page: PRE_UNDERWRITING_COMPANY_FINANCIALS}))
      }
    },
})

export const {aSubmit: aSavePage, aReset} = actions

export default reducer as Reducer

export const {
  selectUpdatePending: selectSavePending,

  selectUpdateErrorMessage: selectSaveErrorMessage,
} = selectors
